/* ============================================================
   Agent visualizer core — COPY of the canonical implementation in
   searchai/meetingai/src/meeting/auraCore.js (keep in sync).
   Used by the admin agent-config page for the live preview.
   ============================================================ */
const VERT = `
attribute vec2 aPos;
void main() { gl_Position = vec4(aPos, 0.0, 1.0); }
`;

// Fragment shader body is verbatim from the Agents-UI aura component.
const FRAG = `
precision highp float;
uniform vec2 iResolution;
uniform float iTime;
uniform float uSpeed, uBlur, uScale, uShape, uFrequency, uAmplitude, uBloom,
              uMix, uSpacing, uColorShift, uVariance, uSmoothing, uMode;
uniform vec3 uColor;

const float TAU = 6.283185;

vec2 randFibo(vec2 p) {
  p = fract(p * vec2(443.897, 441.423));
  p += dot(p, p.yx + 19.19);
  return fract((p.xx + p.yx) * p.xy);
}
vec3 Tonemap(vec3 x) { x *= 4.0; return x / (1.0 + x); }
float luma(vec3 color) { return dot(color, vec3(0.299, 0.587, 0.114)); }
vec3 rgb2hsv(vec3 c) {
  vec4 K = vec4(0.0, -1.0 / 3.0, 2.0 / 3.0, -1.0);
  vec4 p = mix(vec4(c.bg, K.wz), vec4(c.gb, K.xy), step(c.b, c.g));
  vec4 q = mix(vec4(p.xyw, c.r), vec4(c.r, p.yzx), step(p.x, c.r));
  float d = q.x - min(q.w, q.y);
  float e = 1.0e-10;
  return vec3(abs(q.z + (q.w - q.y) / (6.0 * d + e)), d / (q.x + e), q.x);
}
vec3 hsv2rgb(vec3 c) {
  vec4 K = vec4(1.0, 2.0 / 3.0, 1.0 / 3.0, 3.0);
  vec3 p = abs(fract(c.xxx + K.xyz) * 6.0 - K.www);
  return c.z * mix(K.xxx, clamp(p - K.xxx, 0.0, 1.0), c.y);
}
float sdCircle(vec2 st, float r) { return length(st) - r; }
float sdLine(vec2 p, float r) {
  float halfLen = r * 2.0;
  vec2 a = vec2(-halfLen, 0.0);
  vec2 b = vec2(halfLen, 0.0);
  vec2 pa = p - a;
  vec2 ba = b - a;
  float h = clamp(dot(pa, ba) / dot(ba, ba), 0.0, 1.0);
  return length(pa - ba * h);
}
float getSdf(vec2 st) {
  if (uShape == 2.0) return sdLine(st, uScale);
  return sdCircle(st, uScale);
}
vec2 turb(vec2 pos, float t, float it) {
  mat2 rotation = mat2(0.6, -0.25, 0.25, 0.9);
  mat2 layerRotation = mat2(0.6, -0.8, 0.8, 0.6);
  float frequency = mix(2.0, 15.0, uFrequency);
  float amplitude = uAmplitude;
  float frequencyGrowth = 1.4;
  float animTime = t * 0.1 * uSpeed;
  for (int i = 0; i < 4; i++) {
    vec2 rotatedPos = pos * rotation;
    vec2 wave = sin(frequency * rotatedPos + float(i) * animTime + it);
    pos += (amplitude / frequency) * rotation[0] * wave;
    rotation *= layerRotation;
    amplitude *= mix(1.0, max(wave.x, wave.y), uVariance);
    frequency *= frequencyGrowth;
  }
  return pos;
}

const float ITERATIONS = 36.0;

void main() {
  vec2 uv = gl_FragCoord.xy / iResolution.xy;
  vec3 pp = vec3(0.0);
  vec3 bloom = vec3(0.0);
  float t = iTime * 0.5;
  vec2 pos = uv - 0.5;
  vec2 prevPos = turb(pos, t, 0.0 - 1.0 / ITERATIONS);
  float spacing = mix(1.0, TAU, uSpacing);
  for (float i = 1.0; i < ITERATIONS + 1.0; i++) {
    float iter = i / ITERATIONS;
    vec2 st = turb(pos, t, iter * spacing);
    float d = abs(getSdf(st));
    float pd = distance(st, prevPos);
    prevPos = st;
    float dynamicBlur = exp2(pd * 2.0 * 1.4426950408889634) - 1.0;
    float ds = smoothstep(0.0, uBlur * 0.05 + max(dynamicBlur * uSmoothing, 0.001), d);
    vec3 color = uColor;
    if (uColorShift > 0.01) {
      vec3 hsv = rgb2hsv(color);
      hsv.x = fract(hsv.x + (1.0 - iter) * uColorShift * 0.3);
      color = hsv2rgb(hsv);
    }
    float invd = 1.0 / max(d + dynamicBlur, 0.001);
    pp += (ds - 1.0) * color;
    bloom += clamp(invd, 0.0, 250.0) * color;
  }
  pp *= 1.0 / ITERATIONS;
  vec3 color;
  // Dark mode only — every surface we render on is a dark tile.
  bloom = bloom / (bloom + 2e4);
  color = (-pp + bloom * 3.0 * uBloom) * 1.2;
  color += (randFibo(gl_FragCoord.xy).x - 0.5) / 255.0;
  color = Tonemap(color);
  float alpha = luma(color) * uMix;
  gl_FragColor = vec4(color * uMix, alpha);
}
`;

// Per-state motion targets — mirrors use-agent-audio-visualizer-aura.ts.
const STATE_PARAMS = {
  // idle: a calm, slow version of the listening ring — the upstream preset
  // (speed 10 / amp 1.2 / scale .2) collapses into a smeared blob at rest.
  idle:       { speed: 14, scale: 0.28, amplitude: 0.9, frequency: 0.6, brightness: 1.2, pulse: null },
  disconnected: { speed: 14, scale: 0.28, amplitude: 0.9, frequency: 0.6, brightness: 1.0, pulse: null },
  listening:  { speed: 20, scale: 0.3, amplitude: 1.0, frequency: 0.7, brightness: 1.75, pulse: [1.5, 2.0] },
  thinking:   { speed: 30, scale: 0.3, amplitude: 0.5, frequency: 1.0, brightness: 1.5, pulse: [0.5, 2.5] },
  connecting: { speed: 30, scale: 0.3, amplitude: 0.5, frequency: 1.0, brightness: 1.5, pulse: [0.5, 2.5] },
  // speaking: keep the SHAPE stable (near-listening speed) — the rhythm
  // comes from voice volume driving scale, not from fast turbulence, which
  // read as unstable spinning.
  speaking:   { speed: 26, scale: 0.3, amplitude: 0.8, frequency: 1.0, brightness: 1.6, pulse: null },
};
const PULSE_PERIOD_MS = 700; // 0.35s each way, mirrored

function hexToRgb(hex) {
  const m = /^#([0-9a-fA-F]{2})([0-9a-fA-F]{2})([0-9a-fA-F]{2})$/.exec(hex || '');
  if (!m) return [0.12, 0.84, 0.98];
  return [1, 2, 3].map((i) => parseInt(m[i], 16) / 255);
}

export function createAura(container, { color = '#f97316', colorShift = 0.05 } = {}) {
  const canvas = document.createElement('canvas');
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';
  container.appendChild(canvas);

  const gl = canvas.getContext('webgl', { alpha: true, premultipliedAlpha: true, antialias: false });
  if (!gl) { canvas.remove(); return null; }

  function compile(type, src) {
    const s = gl.createShader(type);
    gl.shaderSource(s, src);
    gl.compileShader(s);
    if (!gl.getShaderParameter(s, gl.COMPILE_STATUS)) throw new Error(gl.getShaderInfoLog(s) || 'shader');
    return s;
  }
  let prog;
  try {
    prog = gl.createProgram();
    gl.attachShader(prog, compile(gl.VERTEX_SHADER, VERT));
    gl.attachShader(prog, compile(gl.FRAGMENT_SHADER, FRAG));
    gl.linkProgram(prog);
    if (!gl.getProgramParameter(prog, gl.LINK_STATUS)) throw new Error('link');
  } catch (e) {
    canvas.remove();
    return null;
  }
  gl.useProgram(prog);

  const quad = gl.createBuffer();
  gl.bindBuffer(gl.ARRAY_BUFFER, quad);
  gl.bufferData(gl.ARRAY_BUFFER, new Float32Array([-1, -1, 3, -1, -1, 3]), gl.STATIC_DRAW);
  const aPos = gl.getAttribLocation(prog, 'aPos');
  gl.enableVertexAttribArray(aPos);
  gl.vertexAttribPointer(aPos, 2, gl.FLOAT, false, 0, 0);
  gl.enable(gl.BLEND);
  gl.blendFunc(gl.ONE, gl.ONE_MINUS_SRC_ALPHA);

  const U = {};
  ['iResolution', 'iTime', 'uSpeed', 'uBlur', 'uScale', 'uShape', 'uFrequency', 'uAmplitude',
    'uBloom', 'uMix', 'uSpacing', 'uColorShift', 'uVariance', 'uSmoothing', 'uMode', 'uColor']
    .forEach((n) => { U[n] = gl.getUniformLocation(prog, n); });

  const rgb = hexToRgb(color);
  // Current (animated) and target params.
  const cur = { speed: 20, scale: 0.3, amplitude: 1.0, frequency: 0.7, brightness: 1.75 };
  let target = STATE_PARAMS.listening;
  let volume = 0;
  let raf = 0;
  let destroyed = false;
  const t0 = performance.now();

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(container.clientWidth * dpr));
    const h = Math.max(1, Math.round(container.clientHeight * dpr));
    if (canvas.width !== w || canvas.height !== h) {
      canvas.width = w;
      canvas.height = h;
      gl.viewport(0, 0, w, h);
    }
  }

  function frame(now) {
    if (destroyed) return;
    resize();
    if (analyser) sampleAudio();
    const dt = 1 / 60;
    const ease = 1 - Math.pow(0.02, dt); // ~fast easeOut toward targets
    cur.speed += (target.speed - cur.speed) * ease;
    cur.amplitude += (target.amplitude - cur.amplitude) * ease;
    cur.frequency += (target.frequency - cur.frequency) * ease;

    // Speaking: volume drives scale directly (0.2 + 0.2*vol), like the hook.
    const scaleTarget = target === STATE_PARAMS.speaking && volume > 0.02
      ? 0.2 + 0.2 * Math.min(volume, 1)
      : target.scale;
    cur.scale += (scaleTarget - cur.scale) * (ease * 1.6);

    let brightness;
    if (target.pulse) {
      const [lo, hi] = target.pulse;
      const phase = (now % PULSE_PERIOD_MS) / PULSE_PERIOD_MS;
      brightness = lo + (hi - lo) * (0.5 - 0.5 * Math.cos(phase * Math.PI * 2));
      cur.brightness = brightness;
    } else {
      cur.brightness += (target.brightness - cur.brightness) * ease;
      brightness = cur.brightness;
    }

    gl.clearColor(0, 0, 0, 0);
    gl.clear(gl.COLOR_BUFFER_BIT);
    gl.uniform2f(U.iResolution, canvas.width, canvas.height);
    gl.uniform1f(U.iTime, (now - t0) / 1000);
    gl.uniform1f(U.uSpeed, cur.speed);
    gl.uniform1f(U.uBlur, 0.2);
    gl.uniform1f(U.uScale, cur.scale);
    gl.uniform1f(U.uShape, 1.0);
    gl.uniform1f(U.uFrequency, cur.frequency);
    gl.uniform1f(U.uAmplitude, cur.amplitude);
    gl.uniform1f(U.uBloom, 0.0);
    gl.uniform1f(U.uMix, brightness);
    gl.uniform1f(U.uSpacing, 0.5);
    gl.uniform1f(U.uColorShift, Math.max(0, Math.min(1, colorShift)));
    gl.uniform1f(U.uVariance, 0.1);
    gl.uniform1f(U.uSmoothing, 1.0);
    gl.uniform1f(U.uMode, 0.0);
    gl.uniform3f(U.uColor, rgb[0], rgb[1], rgb[2]);
    gl.drawArrays(gl.TRIANGLES, 0, 3);
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  // Optional live-audio rhythm: an analyser on the agent's MediaStreamTrack
  // drives `volume` every frame, so speaking pulses WITH the voice.
  let audioCtx = null;
  let analyser = null;
  let audioBuf = null;
  function detachAudio() {
    try { audioCtx?.close(); } catch (_) {}
    audioCtx = null;
    analyser = null;
    audioBuf = null;
    volume = 0;
  }
  function sampleAudio() {
    if (!analyser) return;
    analyser.getByteTimeDomainData(audioBuf);
    let sum = 0;
    for (let i = 0; i < audioBuf.length; i++) {
      const d = (audioBuf[i] - 128) / 128;
      sum += d * d;
    }
    // RMS → perceptual-ish 0..1
    volume = Math.min(1, Math.sqrt(sum / audioBuf.length) * 4);
  }

  return {
    setState(state) {
      target = STATE_PARAMS[state] || STATE_PARAMS.listening;
    },
    setVolume(v) { volume = v; },
    attachAudioTrack(mediaStreamTrack) {
      detachAudio();
      if (!mediaStreamTrack) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioCtx = new Ctx();
        const src = audioCtx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.6;
        src.connect(analyser);
        audioBuf = new Uint8Array(analyser.fftSize);
      } catch (_) { detachAudio(); }
    },
    destroy() {
      destroyed = true;
      detachAudio();
      cancelAnimationFrame(raf);
      canvas.remove();
      const lose = gl.getExtension('WEBGL_lose_context');
      if (lose) lose.loseContext();
    },
  };
}

/* ============================================================
   Canvas-2D visualizer styles — wave / radial / grid / bar.
   Same state machine + audio tap as the aura, no WebGL needed.
   Look is modeled on the LiveKit Agents-UI equivalents.
   ============================================================ */
function createCanvasVisualizer(container, { style = 'bar', color = '#f97316' } = {}) {
  const canvas = document.createElement('canvas');
  canvas.style.width = '100%';
  canvas.style.height = '100%';
  canvas.style.display = 'block';
  container.appendChild(canvas);
  const ctx = canvas.getContext('2d');
  if (!ctx) { canvas.remove(); return null; }

  let state = 'listening';
  let volume = 0;
  let energy = 0.2;
  let raf = 0;
  let destroyed = false;
  const t0 = performance.now();

  let audioCtx = null;
  let analyser = null;
  let audioBuf = null;
  function detachAudio() {
    try { audioCtx?.close(); } catch (_) {}
    audioCtx = null; analyser = null; audioBuf = null; volume = 0;
  }
  function sampleAudio() {
    if (!analyser) return;
    analyser.getByteTimeDomainData(audioBuf);
    let sum = 0;
    for (let i = 0; i < audioBuf.length; i++) {
      const d = (audioBuf[i] - 128) / 128;
      sum += d * d;
    }
    volume = Math.min(1, Math.sqrt(sum / audioBuf.length) * 4);
  }

  function resize() {
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    const w = Math.max(1, Math.round(container.clientWidth * dpr));
    const h = Math.max(1, Math.round(container.clientHeight * dpr));
    if (canvas.width !== w || canvas.height !== h) { canvas.width = w; canvas.height = h; }
  }

  function frame(now) {
    if (destroyed) return;
    resize();
    if (analyser) sampleAudio();
    const t = (now - t0) / 1000;
    const w = canvas.width;
    const h = canvas.height;

    const pulsing = state === 'thinking' || state === 'connecting';
    const targetE = state === 'speaking' ? 0.25 + 0.75 * volume
      : pulsing ? 0.25 + 0.45 * (0.5 + 0.5 * Math.sin(t * 4.2))
      : state === 'listening' ? 0.32 + 0.12 * Math.sin(t * 1.6)
      : 0.18 + 0.06 * Math.sin(t * 0.9); // idle / disconnected
    energy += (targetE - energy) * 0.18;
    const e = Math.max(0.05, Math.min(1, energy));

    ctx.clearRect(0, 0, w, h);
    ctx.fillStyle = color;
    ctx.strokeStyle = color;

    if (style === 'bar') {
      const n = 5;
      const weights = [0.6, 0.85, 1, 0.85, 0.6];
      const bw = w * 0.09;
      const gap = w * 0.07;
      const total = n * bw + (n - 1) * gap;
      const x0 = (w - total) / 2;
      const hot = pulsing ? Math.floor(t * 6) % n : -1;
      for (let i = 0; i < n; i++) {
        const wob = 0.82 + 0.18 * Math.sin(t * 2.1 + i * 1.35);
        let bh = Math.max(bw, h * 0.62 * e * weights[i] * wob);
        ctx.globalAlpha = i === hot ? 1 : 0.85;
        if (i === hot) bh = Math.max(bh, h * 0.3);
        const x = x0 + i * (bw + gap);
        const y = (h - bh) / 2;
        const r = bw / 2;
        ctx.beginPath();
        ctx.moveTo(x, y + r);
        ctx.arcTo(x, y, x + bw, y, r);
        ctx.arcTo(x + bw, y, x + bw, y + bh, r);
        ctx.arcTo(x + bw, y + bh, x, y + bh, r);
        ctx.arcTo(x, y + bh, x, y, r);
        ctx.fill();
      }
    } else if (style === 'wave') {
      const mid = h / 2;
      const amp = h * 0.3 * e;
      ctx.lineWidth = Math.max(2, h * 0.035);
      ctx.lineCap = 'round';
      ctx.globalAlpha = 0.95;
      ctx.beginPath();
      const steps = 64;
      for (let i = 0; i <= steps; i++) {
        const fx = i / steps;
        const win = Math.sin(Math.PI * fx); // hann-ish window
        const y = mid + Math.sin(fx * 9 + t * (pulsing ? 6 : 3)) * amp * win;
        const x = w * 0.08 + fx * w * 0.84;
        if (i === 0) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    } else if (style === 'radial') {
      const cx = w / 2;
      const cy = h / 2;
      const min = Math.min(w, h);
      const r0 = min * 0.2;
      const n = 28;
      ctx.lineWidth = Math.max(2, min * 0.03);
      ctx.lineCap = 'round';
      for (let i = 0; i < n; i++) {
        const a = (i / n) * Math.PI * 2;
        const sweep = pulsing ? (0.5 + 0.5 * Math.cos(a - t * 3.5)) : 1;
        const len = min * 0.05 + min * 0.22 * e * (0.55 + 0.45 * Math.sin(t * 2.2 + i * 0.75)) * sweep;
        ctx.globalAlpha = 0.45 + 0.55 * sweep;
        ctx.beginPath();
        ctx.moveTo(cx + Math.cos(a) * r0, cy + Math.sin(a) * r0);
        ctx.lineTo(cx + Math.cos(a) * (r0 + len), cy + Math.sin(a) * (r0 + len));
        ctx.stroke();
      }
    } else { // grid
      const n = 5;
      const min = Math.min(w, h);
      const cell = min / (n + 1);
      const r = cell * 0.22;
      const ox = (w - cell * (n - 1)) / 2;
      const oy = (h - cell * (n - 1)) / 2;
      for (let gy = 0; gy < n; gy++) {
        for (let gx = 0; gx < n; gx++) {
          const dx = gx - (n - 1) / 2;
          const dy = gy - (n - 1) / 2;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const ripple = 0.5 + 0.5 * Math.sin(t * (pulsing ? 5 : 3) - dist * 1.7);
          ctx.globalAlpha = 0.18 + 0.82 * e * ripple;
          ctx.beginPath();
          ctx.arc(ox + gx * cell, oy + gy * cell, r * (0.7 + 0.5 * e * ripple), 0, Math.PI * 2);
          ctx.fill();
        }
      }
    }
    ctx.globalAlpha = 1;
    raf = requestAnimationFrame(frame);
  }
  raf = requestAnimationFrame(frame);

  return {
    setState(s) { state = s || 'listening'; },
    setVolume(v) { volume = v; },
    attachAudioTrack(mediaStreamTrack) {
      detachAudio();
      if (!mediaStreamTrack) return;
      try {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        audioCtx = new Ctx();
        const src = audioCtx.createMediaStreamSource(new MediaStream([mediaStreamTrack]));
        analyser = audioCtx.createAnalyser();
        analyser.fftSize = 512;
        analyser.smoothingTimeConstant = 0.6;
        src.connect(analyser);
        audioBuf = new Uint8Array(analyser.fftSize);
      } catch (_) { detachAudio(); }
    },
    destroy() {
      destroyed = true;
      detachAudio();
      cancelAnimationFrame(raf);
      canvas.remove();
    },
  };
}

/**
 * Unified factory — style comes from the agent\'s admin config
 * (visualizerConfig: enabled/style/color/colorShift).
 * \'aura\' uses the WebGL shader (falls back to \'radial\' without WebGL);
 * wave/radial/grid/bar are canvas-2D.
 */
export function createAgentVisualizer(container, opts = {}) {
  const style = opts.style || 'aura';
  if (style === 'aura') {
    const a = createAura(container, opts);
    if (a) return a;
    return createCanvasVisualizer(container, { ...opts, style: 'radial' });
  }
  return createCanvasVisualizer(container, opts);
}
